import { useEffect } from 'react';
import { useTranslation } from '../../components/Translation';
import {
  formatPublicationDate,
  getPublicationAuthors,
  getPublicationTags,
  getPublicationTitle,
  getPublicationTypeTranslationKey,
  Publication,
} from '../../constants/publications/publications';

export default function PublicationModal({ pub, onClose }: { pub: Publication; onClose: () => void }) {
  const { language, t } = useTranslation();
  const title = getPublicationTitle(pub, t);
  const authors = getPublicationAuthors(pub, t);
  const tags = getPublicationTags(pub, t);
  const pdfUrl = pub.pdfPath ?? pub.pdfUrl;
  const date = formatPublicationDate(pub.date, language, 'long');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(13,31,53,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-2xl p-8"
        style={{
          background: 'rgba(255,255,255,0.97)',
          boxShadow: '0 24px 64px rgba(13,31,53,0.2)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors text-sm font-bold"
        >
          X
        </button>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className="text-micro font-bold uppercase text-azul-uady tracking-widest">
            {t(`publicaciones.${getPublicationTypeTranslationKey(pub.type)}`)}
          </span>
          {tags.map(tag => (
            <span key={tag} className="text-micro font-bold uppercase text-dorado-uady tracking-widest">
              {tag}
            </span>
          ))}
        </div>

        <h2 className="text-2xl font-bold text-azul-uady leading-snug mb-3">
          {title}
        </h2>

        {pub.doi && (
          <p className="text-micro text-gray-500 mb-3">
            DOI:{' '}
            <a href={pub.doi} target="_blank" rel="noopener noreferrer"
              className="text-azul-uady hover:underline">
              {pub.doi.replace(/^https?:\/\/doi\.org\//, '')}
            </a>
          </p>
        )}

        {authors && <p className="text-mini text-gray-500 mb-1 italic">{authors}</p>}
        {pub.venue && (
          <p className="text-micro text-gray-500 mb-1">
            {t('publicaciones.journal_event')}: {pub.venue}
          </p>
        )}
        {pub.publicationDetails && (
          <p className="text-micro text-gray-500 mb-1">
            {t('publicaciones.detalles_publicacion')}: {pub.publicationDetails}
          </p>
        )}
        {date && <p className="text-micro text-gray-400 mb-6">{date}</p>}

        <div className="w-12 h-1 bg-dorado-uady rounded mb-6" />

        {(pub.abstract || pub.abstractKey) && (
          <p className="text-sm text-gray-700 leading-relaxed text-justify">
            {pub.abstract ?? (pub.abstractKey ? t(`publicaciones.${pub.abstractKey}`) : '')}
          </p>
        )}

        {pdfUrl && (
          <div className="mt-6">
            <iframe
              src={pdfUrl}
              title={title}
              className="w-full h-[60vh] min-h-[320px] rounded-xl border border-gray-200 bg-white"
            >
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                {t('publicaciones.ver_articulo')}
              </a>
            </iframe>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-8">
          {pdfUrl && (
            <a href={pdfUrl} download={Boolean(pub.pdfPath)} target={pub.pdfPath ? undefined : "_blank"}
              rel={pub.pdfPath ? undefined : "noopener noreferrer"}
              className="inline-flex items-center gap-2 bg-dorado-uady text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-dorado-uady/90 transition-colors">
              {t('publicaciones.descargar')}
            </a>
          )}
          {pub.researchGateUrl && (
            <a href={pub.researchGateUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-azul-uady text-white text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-azul-uady/90 transition-colors">
              {pub.researchGateFullTextAvailable
                ? t('publicaciones.rg_full_text')
                : t('publicaciones.view_rg')}
            </a>
          )}
          {pub.publicAccessUrl && (
            <a href={pub.publicAccessUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-azul-uady text-azul-uady text-sm font-semibold px-5 py-2.5 rounded-xl hover:bg-azul-uady/5 transition-colors">
              {t('publicaciones.acceso_abierto')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}